import React from 'react';
import {
  View,
  StyleSheet,
} from 'react-native';
import { Text, TextInput } from '@/components/common/text';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';
import { postItemStrings } from '@/constants/residents/marketplaceStrings';
import { POST_ITEM_CONDITIONS } from '@/constants/residents/marketplaceConstants';
import { PostItemFormData, PostItemCondition, PostItemPriceType, getPostItemConditionDisplayText } from '@/types/residents/marketplaceListing';
import { StepHeader, SelectableOption, postItemStyles } from './shared';
import { ToggleBar } from '../shared';

interface StepPriceConditionProps {
  formData: PostItemFormData;
  onFormChange: (updates: Partial<PostItemFormData>) => void;
}

export const StepPriceCondition: React.FC<StepPriceConditionProps> = ({
  formData,
  onFormChange,
}) => {
  const MARKETPLACE_COLORS = useMarketplaceColors();

  const handlePriceTypeChange = (type: 'set_price' | 'free') => {
    onFormChange({
      priceType: type,
      price: type === 'free' ? '0' : formData.price,
    });
  };

  const handleConditionChange = (condition: PostItemCondition) => {
    onFormChange({ condition });
  };

  const handlePriceChange = (text: string) => {
    // Only allow numbers and decimal point
    const cleaned = text.replace(/[^0-9.]/g, '');
    // Prevent multiple decimal points
    const parts = cleaned.split('.');
    const formatted = parts.length > 2
      ? parts[0] + '.' + parts.slice(1).join('')
      : cleaned;
    onFormChange({ price: formatted });
  };

  return (
    <View style={postItemStyles.container}>
      <StepHeader title={postItemStrings.details} />

      <View style={styles.content}>
        {/* Price Type Toggle */}
        <ToggleBar
          options={[
            { key: 'set_price', label: postItemStrings.setPrice },
            { key: 'free', label: postItemStrings.freeItem },
          ]}
          activeKey={formData.priceType}
          onSelect={(key) => handlePriceTypeChange(key as PostItemPriceType)}
        />

        {/* Price Input (only shown when set_price is selected) */}
        {formData.priceType === 'set_price' && (
          <View style={[
            styles.priceInputContainer,
            {
              backgroundColor: MARKETPLACE_COLORS.inputBackground,
              borderColor: MARKETPLACE_COLORS.inputBorder,
            },
          ]}>
            <Text style={[styles.currencySymbol, { color: MARKETPLACE_COLORS.textSecondary }]}>$</Text>
            <TextInput
              style={[styles.priceInput, { color: MARKETPLACE_COLORS.textPrimary }]}
              value={formData.price}
              onChangeText={handlePriceChange}
              placeholder={postItemStrings.pricePlaceholder}
              placeholderTextColor={MARKETPLACE_COLORS.textSecondary}
              keyboardType="decimal-pad"
              textAlign="right"
            />
          </View>
        )}
      </View>

      {/* Condition Section */}
      <View style={styles.conditionSection}>
        <View style={postItemStyles.optionsList}>
          {POST_ITEM_CONDITIONS.map((condition) => (
            <SelectableOption
              key={condition}
              label={getPostItemConditionDisplayText(condition)}
              isSelected={formData.condition === condition}
              onPress={() => handleConditionChange(condition)}
              type="radio"
            />
          ))}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  content: {
    paddingHorizontal: 24,
    gap: 16,
  },
  priceInputContainer: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 16,
    minHeight: 40,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  currencySymbol: {
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 17.5,
  },
  priceInput: {
    flex: 1,
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 17.5,
    padding: 0,
    marginLeft: 8,
  },
  conditionSection: {
    paddingHorizontal: 24,
    paddingVertical: 24,
  },
});
